import { Button } from "@/components/ui/button";
import { MessageCircle, Phone, Users, Zap, ShieldCheck, Clock, MapPin, Handshake, Headphones, MapPinned } from "lucide-react";
import heroImage from "@/assets/hero-industrial.jpg";
import proofWorker from "@/assets/proof-worker.jpg";
import proofEquipment from "@/assets/proof-equipment.jpg";
import proofPlant from "@/assets/proof-plant.jpg";
import { useLanguage } from "@/contexts/LanguageContext";
import { trackConversion } from "@/config/analytics";
import { PHONE_DISPLAY, TEL_HREF, buildWhatsAppUrl } from "@/lib/phone";

const WHATSAPP_URL = buildWhatsAppUrl("Hello Arabian Al Waha, I'd like a quote.");

const Hero = () => {
  const { language } = useLanguage();
  const isAr = language === "ar";
  const t = {
    title1: isAr ? "قوى عاملة فورية ودعم للمشاريع" : "Immediate Manpower & Project Support",
    title2: isAr ? "في المملكة" : "in KSA",
    subtitle: isAr
      ? "جاهزون لأعمال التوقف والأعمال الصناعية — تعبئة سريعة، فريق جاهز، نشر فوري"
      : "Ready for Shutdown & Industrial Work — Fast Mobilization. Ready Team. Immediate Deployment.",
    cities: isAr ? "الجبيل | ينبع | الدمام" : "Jubail | Yanbu | Dammam",
    credibility: isAr
      ? "ندعم المشاريع الصناعية والإنشائية في جميع أنحاء المملكة بحلول قوى عاملة سريعة وموثوقة."
      : "Supporting industrial and construction projects across KSA with fast and reliable manpower solutions.",
    f1t: isAr ? "قوى عاملة ماهرة" : "SKILLED MANPOWER",
    f1s: isAr ? "جاهزة للنشر" : "Ready to Deploy",
    f2t: isAr ? "تعبئة سريعة" : "FAST MOBILIZATION",
    f2s: isAr ? "دعم فوري" : "Immediate Support",
    f3t: isAr ? "آمن وموثوق" : "SAFE & RELIABLE",
    f3s: isAr ? "جودة تثق بها" : "Quality You Can Trust",
    quote: isAr ? "احصل على عرض عبر واتساب" : "Get Quote on WhatsApp",
    call: isAr ? "اتصل فوراً" : "Contact Immediately",
    available: isAr ? "متاحون اليوم" : "Available Today",
    s1t: isAr ? "متاح للنشر" : "AVAILABLE FOR",
    s1s: isAr ? "الفوري" : "IMMEDIATE DEPLOYMENT",
    s2t: isAr ? "نخدم" : "SERVING",
    s2s: "JUBAIL | YANBU | DAMMAM",
    s3t: isAr ? "ملتزمون بـ" : "COMMITTED TO",
    s3s: isAr ? "الجودة والسلامة" : "QUALITY & SAFETY",
    s4t: isAr ? "دعم 24/7" : "24/7 SUPPORT",
    s4s: PHONE_DISPLAY,
    imgAlt: isAr ? "عمال صناعيون في المملكة" : "Industrial workers at a Saudi refinery",
  };

  const features = [
    { icon: Users, title: t.f1t, sub: t.f1s },
    { icon: Zap, title: t.f2t, sub: t.f2s },
    { icon: ShieldCheck, title: t.f3t, sub: t.f3s },
  ];

  const stats = [
    { icon: Clock, title: t.s1t, sub: t.s1s },
    { icon: MapPin, title: t.s2t, sub: t.s2s },
    { icon: Handshake, title: t.s3t, sub: t.s3s },
    { icon: Headphones, title: t.s4t, sub: t.s4s },
  ];

  return (
    <section id="hero" className="relative bg-primary-darker text-primary-foreground pt-20">
      {/* Hero main */}
      <div className="relative overflow-hidden">
        {/* Background image (full bleed on mobile, right-side overlay on desktop) */}
        <div className="absolute inset-0">
          <img
            src={heroImage}
            alt={t.imgAlt}
            className="w-full h-full object-cover lg:object-right brightness-110"
            loading="eager"
            {...{ fetchpriority: "high" } as any}
            width={1920}
            height={1080}
          />
          {/* Mobile: dim entire image. Desktop: gradient from solid left to transparent right */}
          <div className="absolute inset-0 bg-primary-darker/25 lg:bg-gradient-to-r lg:from-primary-darker/85 lg:via-primary-darker/35 lg:to-transparent" />
          {/* Extra readability gradient on mobile (top darker for headline) */}
          <div className="absolute inset-0 bg-gradient-to-t from-primary-darker/65 via-primary-darker/10 to-transparent lg:hidden" />
        </div>

        <div className="container mx-auto px-6 lg:px-8 relative z-10 py-16 lg:py-24">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2.5 bg-accent/20 border-2 border-accent/60 rounded-full px-6 py-2.5 mb-6 backdrop-blur-sm shadow-lg shadow-accent/20">
              <span className="w-3 h-3 rounded-full bg-accent animate-pulse" />
              <span className="text-base sm:text-lg font-extrabold tracking-widest text-accent uppercase">{t.available}</span>
            </div>
            <h1 className="font-montserrat font-black uppercase text-[2.25rem] sm:text-[3rem] lg:text-[3.75rem] xl:text-[4.25rem] leading-[1.05] mb-5 text-accent [text-shadow:0_2px_8px_rgba(0,0,0,0.6),0_4px_24px_rgba(0,0,0,0.5)]">
              {t.title1} <span className="text-accent">{t.title2}</span>
            </h1>
            <p className="text-lg sm:text-xl lg:text-2xl text-primary-foreground mb-4 leading-snug font-bold drop-shadow-lg">
              {t.subtitle}
            </p>
            <p className="flex items-center gap-2.5 text-xl sm:text-2xl lg:text-3xl text-accent font-extrabold mb-5 drop-shadow-lg">
              <MapPinned className="w-7 h-7" /> {t.cities}
            </p>
            <p className="text-base sm:text-lg text-primary-foreground/95 mb-7 leading-relaxed font-semibold border-l-4 border-accent pl-4">
              {t.credibility}
            </p>

            {/* Feature pills */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-8">
              {features.map((f) => (
                <div
                  key={f.title}
                  className="flex items-center gap-3 bg-primary-foreground/10 border-2 border-accent/40 rounded-lg px-4 py-3.5 backdrop-blur-sm"
                >
                  <span className="w-12 h-12 shrink-0 rounded-full bg-accent/20 border-2 border-accent/60 flex items-center justify-center">
                    <f.icon className="w-6 h-6 text-accent" />
                  </span>
                  <span className="leading-tight">
                    <span className="block text-base sm:text-lg font-extrabold tracking-wide text-primary-foreground uppercase">
                      {f.title}
                    </span>
                    <span className="block text-sm sm:text-base font-semibold text-accent">{f.sub}</span>
                  </span>
                </div>
              ))}
            </div>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                asChild
                size="lg"
                className="bg-[#25D366] hover:bg-[#1ebe57] text-white font-extrabold px-10 py-8 text-lg uppercase tracking-wide shadow-2xl shadow-[#25D366]/40 ring-2 ring-white/20 animate-pulse-slow"
              >
                <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" onClick={() => trackConversion("whatsapp_click")}>
                  <MessageCircle className="w-6 h-6 mr-2" />
                  {t.quote}
                </a>
              </Button>
              <Button
                asChild
                size="lg"
                variant="outline"
                className="border-2 border-accent text-accent hover:bg-accent hover:text-primary-darker bg-transparent font-bold px-8 py-7 text-base uppercase tracking-wide"
              >
                <a href={TEL_HREF} onClick={() => trackConversion("phone_click")}>
                  <Phone className="w-5 h-5 mr-2" />
                  {t.call}: <bdi dir="ltr" className="font-mono">{PHONE_DISPLAY}</bdi>
                </a>
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Proof strip - real industrial visuals */}
      <div className="bg-primary-darker border-t border-accent/20">
        <div className="container mx-auto px-6 lg:px-8 py-6">
          <div className="grid grid-cols-3 gap-3 sm:gap-4">
            {[
              { src: proofWorker, alt: isAr ? "عامل بمعدات السلامة" : "Worker in PPE", label: isAr ? "فرق ماهرة" : "Skilled Crews" },
              { src: proofEquipment, alt: isAr ? "معدات صناعية" : "Industrial equipment", label: isAr ? "معدات جاهزة" : "Equipment Ready" },
              { src: proofPlant, alt: isAr ? "محطة صناعية" : "Industrial plant", label: isAr ? "مواقع نشطة" : "Live Projects" },
            ].map((p) => (
              <div key={p.label} className="relative rounded-lg overflow-hidden aspect-[4/3] group ring-1 ring-accent/20">
                <img
                  src={p.src}
                  alt={p.alt}
                  loading="lazy"
                  width={1024}
                  height={1024}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-primary-darker via-primary-darker/30 to-transparent" />
                <span className="absolute bottom-2 left-2 right-2 text-center text-xs sm:text-sm font-bold uppercase tracking-wide text-accent">
                  {p.label}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Green stats strip */}
      <div className="bg-primary-darker border-t border-accent/30">
        <div className="container mx-auto px-6 lg:px-8 py-5">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
            {stats.map((s) => (
              <div key={s.title} className="flex items-center gap-3">
                <span className="w-10 h-10 shrink-0 rounded-full bg-accent/15 border border-accent/40 flex items-center justify-center">
                  <s.icon className="w-5 h-5 text-accent" />
                </span>
                <span className="leading-tight">
                  <span className="block text-[11px] font-bold tracking-wider text-primary-foreground/80 uppercase">
                    {s.title}
                  </span>
                  <span className="block text-sm font-bold text-primary-foreground uppercase">
                    {s.sub}
                  </span>
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
